import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { article } from "./articleContent";
import { mainSiteUrl } from "@/mocks/categories";
import JsonLd from "@/components/feature/JsonLd";
import { articleJsonLd, faqJsonLd } from "@/lib/seo";

const comparisonRows = [
  { topic: "Spreadsheet", check: "Source and freshness", why: "Information quality" },
  { topic: "Seller", check: "Reputation and details", why: "Risk evaluation" },
  { topic: "Shipping", check: "Method and cost", why: "Total cost" },
  { topic: "Warehouse", check: "Storage / QC process", why: "Order management" },
  { topic: "Coupon", check: "Validity and terms", why: "Avoid misleading offers" },
];

function FaqAccordion({ items }: { items: typeof article.faqs }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="space-y-3">
      {items.map((faq, i) => (
        <div key={i} className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <button
            type="button"
            onClick={() => setOpenIndex(openIndex === i ? null : i)}
            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            aria-expanded={openIndex === i}
          >
            <span className="text-base font-semibold text-foreground-950">{faq.question}</span>
            <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
              <i className={`text-lg ${openIndex === i ? "ri-subtract-line" : "ri-add-line"}`} aria-hidden="true"></i>
            </span>
          </button>
          {openIndex === i && (
            <p className="border-t border-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-600">
              {faq.answer}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ArticlePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleJsonLd({ title: article.h1, description: article.metaDescription, path: "/usfans-spreadsheet-guide" })} />
      <JsonLd data={faqJsonLd(article.faqs)} />
      <Navbar />
      <main className="mx-auto w-full max-w-3xl px-4 pb-16 pt-24 md:px-6 md:pt-28">
        <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-700">
            USFansLink
          </Link>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm" aria-hidden="true"></i>
          </span>
          <span className="text-foreground-700">USFans Spreadsheet Guide</span>
        </nav>

        <article>
          <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary-700">
            {article.eyebrow}
          </span>

          <h1 className="mt-4 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">{article.h1}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-3-line text-sm" aria-hidden="true"></i>
              </span>
              USFansLink Editorial Team
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
              </span>
              Updated August 22, 2026
            </span>
          </div>

          <p className="mt-8 text-lg leading-relaxed text-foreground-700">{article.intro}</p>

          <div className="mt-6 rounded-2xl border border-primary-200 bg-primary-50 p-5">
            <h2 className="flex items-center gap-2 text-base font-bold text-primary-800">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-lightbulb-flash-line text-lg" aria-hidden="true"></i>
              </span>
              Quick Answer
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">{article.quickAnswer}</p>
          </div>

          {article.sections.map((section, i) => (
            <section key={i} className="mt-10">
              <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">{section.heading}</h2>
              {section.paragraphs.map((p, j) => (
                <p key={j} className="mt-4 text-base leading-relaxed text-foreground-600">
                  {p}
                </p>
              ))}
              {section.bullets && (
                <ul className="mt-4 space-y-2">
                  {section.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-base leading-relaxed text-foreground-600">
                      <span className="w-5 h-5 mt-0.5 flex shrink-0 items-center justify-center text-primary-600">
                        <i className="ri-checkbox-circle-line text-lg" aria-hidden="true"></i>
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              {section.heading === "Comparison: What Each Column Usually Means" && (
                <div className="mt-5 overflow-x-auto rounded-2xl border border-background-200">
                  <table className="w-full min-w-[480px] border-collapse text-left">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="px-4 py-3 text-sm font-bold text-foreground-800">Topic</th>
                        <th className="px-4 py-3 text-sm font-bold text-foreground-800">What to Check</th>
                        <th className="px-4 py-3 text-sm font-bold text-foreground-800">Why It Matters</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonRows.map((row) => (
                        <tr key={row.topic} className="border-t border-background-100">
                          <td className="px-4 py-3 text-sm font-semibold text-foreground-800">{row.topic}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600">{row.check}</td>
                          <td className="px-4 py-3 text-sm text-foreground-600">{row.why}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          <section className="mt-10">
            <h2 className="font-heading text-xl text-foreground-950 md:text-2xl">Frequently Asked Questions</h2>
            <div className="mt-5">
              <FaqAccordion items={article.faqs} />
            </div>
          </section>

          <section className="mt-10 rounded-3xl bg-gradient-to-r from-primary-500 to-accent-500 p-7 text-center">
            <h2 className="font-heading text-xl text-background-50 md:text-2xl">Ready to explore USFans resources?</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-background-50/90">
              Browse the product categories and continue your research on the main website.
            </p>
            <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-bold text-foreground-950 transition-transform hover:scale-105"
              >
                Visit Main Website
              </a>
              <Link
                to="/"
                className="whitespace-nowrap rounded-full border-2 border-background-50 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-background-50 hover:text-foreground-950"
              >
                Back to Home
              </Link>
            </div>
          </section>
        </article>

        <div className="mt-8 flex items-center justify-between rounded-2xl border border-background-200 bg-background-50 p-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-700 hover:underline">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
            </span>
            Back to Homepage
          </Link>
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground-700 hover:text-primary-700"
          >
            kaspreadsheet.com
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-external-link-line text-base" aria-hidden="true"></i>
            </span>
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}